import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, ProgressBar } from 'react-bootstrap';
import { BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../../../services/api';

export const PerformanceReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<any>(null);

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      const tickets = response.data.data || [];
      
      // Calculate performance metrics
      const metrics = calculatePerformanceMetrics(tickets);
      setPerformanceData(metrics);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePerformanceMetrics = (tickets: any[]) => {
    
    // Resolution rate
    const totalTickets = tickets.length;
    const resolvedTickets = tickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length;
    const resolutionRate = totalTickets > 0 ? (resolvedTickets / totalTickets) * 100 : 0;

    // Average response time (mock data - would need actual timestamps)
    const avgResponseTime = 2.5; // hours

    // Average resolution time (mock data)
    const avgResolutionTime = 24; // hours

    // SLA compliance (mock data)
    const slaCompliance = 85; // percentage

    // Response time distribution
    const responseTimeData = [
      { range: '< 1h', count: Math.floor(tickets.length * 0.4) },
      { range: '1-2h', count: Math.floor(tickets.length * 0.3) },
      { range: '2-4h', count: Math.floor(tickets.length * 0.2) },
      { range: '> 4h', count: Math.floor(tickets.length * 0.1) },
    ];

    // Resolution time trend (last 7 days)
    const resolutionTrend = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      resolutionTrend.push({
        date: dateStr,
        resolved: Math.floor(Math.random() * 15) + 5,
        avgTime: Math.floor(Math.random() * 10) + 15,
      });
    }

    // Ticket volume by day
    const volumeTrend = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dayTickets = tickets.filter(t => {
        const ticketDate = new Date(t.createdAt);
        return ticketDate.toDateString() === date.toDateString();
      });
      volumeTrend.push({
        date: dateStr,
        created: dayTickets.length,
        resolved: dayTickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length,
      });
    }

    return {
      resolutionRate,
      avgResponseTime,
      avgResolutionTime,
      slaCompliance,
      responseTimeData,
      resolutionTrend,
      volumeTrend,
      totalTickets,
      resolvedTickets,
    };
  };

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-1">
          <i className="fas fa-tachometer-alt me-2 text-primary"></i>
          Performance Metrics
        </h2>
        <p className="text-muted mb-0 small">Track system efficiency and response times</p>
      </div>

      {/* Key Performance Indicators */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="text-muted mb-1">Resolution Rate</h6>
                  <h3 className="mb-0">{performanceData?.resolutionRate.toFixed(1)}%</h3>
                </div>
                <div className="bg-success rounded-circle p-3">
                  <i className="fas fa-check-double text-white"></i>
                </div>
              </div>
              <ProgressBar 
                now={performanceData?.resolutionRate} 
                variant="success" 
                style={{ height: '6px' }} 
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="text-muted mb-1">Avg Response Time</h6>
                  <h3 className="mb-0">{performanceData?.avgResponseTime}h</h3>
                </div>
                <div className="bg-info rounded-circle p-3">
                  <i className="fas fa-clock text-white"></i>
                </div>
              </div>
              <ProgressBar 
                now={85} 
                variant="info" 
                style={{ height: '6px' }} 
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="text-muted mb-1">Avg Resolution Time</h6>
                  <h3 className="mb-0">{performanceData?.avgResolutionTime}h</h3>
                </div>
                <div className="bg-warning rounded-circle p-3">
                  <i className="fas fa-hourglass-half text-white"></i>
                </div>
              </div>
              <ProgressBar 
                now={70} 
                variant="warning" 
                style={{ height: '6px' }} 
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="text-muted mb-1">SLA Compliance</h6>
                  <h3 className="mb-0">{performanceData?.slaCompliance}%</h3>
                </div>
                <div className="bg-primary rounded-circle p-3">
                  <i className="fas fa-award text-white"></i>
                </div>
              </div>
              <ProgressBar 
                now={performanceData?.slaCompliance} 
                variant="primary" 
                style={{ height: '6px' }} 
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        {/* Response Time Distribution */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-bar me-2 text-primary"></i>
                Response Time Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData?.responseTimeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0dcaf0" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Resolution Trend */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-line me-2 text-success"></i>
                Resolution Trend (Last 7 Days)
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData?.resolutionTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="resolved" stroke="#198754" strokeWidth={2} name="Tickets Resolved" />
                  <Line yAxisId="right" type="monotone" dataKey="avgTime" stroke="#ffc107" strokeWidth={2} name="Avg Time (hrs)" />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Volume Trend */}
        <Col lg={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-area me-2 text-info"></i>
                Ticket Volume Trend
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={performanceData?.volumeTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="created" fill="#0dcaf0" name="Created" />
                  <Bar dataKey="resolved" fill="#198754" name="Resolved" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Performance Summary */}
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-info-circle me-2 text-primary"></i>
                Performance Summary
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Tickets Resolved</span>
                      <span className="fw-bold">{performanceData?.resolvedTickets} / {performanceData?.totalTickets}</span>
                    </div>
                    <ProgressBar 
                      now={performanceData?.resolutionRate} 
                      label={`${performanceData?.resolutionRate.toFixed(1)}%`}
                      variant="success"
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Response Time Goal (&lt; 4h)</span>
                      <span className="fw-bold">85%</span>
                    </div>
                    <ProgressBar now={85} label="85%" variant="info" />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Resolution Time Goal (&lt; 48h)</span>
                      <span className="fw-bold">70%</span>
                    </div>
                    <ProgressBar now={70} label="70%" variant="warning" />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">SLA Compliance Target</span>
                      <span className="fw-bold">{performanceData?.slaCompliance}% / 90%</span>
                    </div>
                    <ProgressBar now={performanceData?.slaCompliance} label={`${performanceData?.slaCompliance}%`} variant="primary" />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

